/**
 * Redux slice for borrower document management
 * Manages various document types required for merchant onboarding and KYC
 */

import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export interface UploadedFile {
  name: string;
  path: string;
}

/**
 * Document state for borrower/merchant onboarding
 * Manages uploaded documents required for KYC and onboarding verification
 */
export interface DocState {
  // Business documents
  business: UploadedFile[];
  share: UploadedFile[];
  balance: UploadedFile[];

  // Proof documents
  proof: UploadedFile[];
  bank: UploadedFile[];
  salaryProof: UploadedFile[];
  financialStatement: UploadedFile[];

  // Investment/Fund manager documents
  investmentMandate: UploadedFile[];
  fundManagerLicense: UploadedFile[];

  // Corporate documents
  certificateOfIncorporation: UploadedFile[];
  supportingDocumentsWithSuppliers: UploadedFile[];
  proofOfBankAccountDetails: UploadedFile[];
  companyConstitution: UploadedFile[];

  // Tax/Compliance documents
  crsSelfCertificationForm: UploadedFile[]; // Common Reporting Standard
}

const initialState: DocState = {
  business: [],
  share: [],
  balance: [],
  proof: [],
  bank: [],
  salaryProof: [],
  financialStatement: [],
  investmentMandate: [],
  fundManagerLicense: [],
  certificateOfIncorporation: [],
  supportingDocumentsWithSuppliers: [],
  proofOfBankAccountDetails: [],
  crsSelfCertificationForm: [],
  companyConstitution: [],
};

/**
 * Generic reducer factory for document array setters
 * Eliminates duplication for simple array assignment reducers
 */
const createDocumentSetter = <K extends keyof DocState>(field: K) => {
  return (state: DocState, action: PayloadAction<UploadedFile[]>) => {
    state[field] = action.payload;
  };
};

const docBorrowerSlice = createSlice({
  name: "bordoc",
  initialState,
  reducers: {
    // Business and profile documents
    setBusinessDoc: createDocumentSetter("business"),
    setShareDoc: createDocumentSetter("share"),
    setBalance: createDocumentSetter("balance"),

    // Identity and address documents
    setProofDoc: createDocumentSetter("proof"),
    setBankDoc: createDocumentSetter("bank"),
    setSalaryProofDoc: createDocumentSetter("salaryProof"),

    // Financial documents
    setFinancialStatementDoc: createDocumentSetter("financialStatement"),
    setInvestmentMandateDoc: createDocumentSetter("investmentMandate"),

    // Corporate documents
    setFundManagerLicenseDoc: createDocumentSetter("fundManagerLicense"),
    setCertificateOfIncorporationDoc: createDocumentSetter("certificateOfIncorporation"),
    setSupportingDocumentsWithSuppliersDoc: createDocumentSetter(
      "supportingDocumentsWithSuppliers",
    ),
    setProofOfBankAccountDetailsDoc: createDocumentSetter("proofOfBankAccountDetails"),
    setCompanyConstitutionDoc: createDocumentSetter("companyConstitution"),

    // Tax/Compliance documents
    setCrsSelfCertificationFormDoc: createDocumentSetter("crsSelfCertificationForm"),

    /**
     * Bulk update multiple document types at once
     * Useful for hydrating state from API response
     */
    hydrateDocuments: (state, action: PayloadAction<Partial<DocState>>) => {
      return { ...state, ...action.payload };
    },

    /**
     * Clear a specific document type
     */
    clearDocumentType: <K extends keyof DocState>(state: DocState, action: PayloadAction<K>) => {
      state[action.payload] = [];
    },

    /**
     * Reset all documents to initial state
     */
    resetAllDocuments: () => initialState,
  },
});

export const {
  setBusinessDoc,
  setShareDoc,
  setBalance,
  setProofDoc,
  setBankDoc,
  setSalaryProofDoc,
  setFinancialStatementDoc,
  setInvestmentMandateDoc,
  setFundManagerLicenseDoc,
  setCertificateOfIncorporationDoc,
  setSupportingDocumentsWithSuppliersDoc,
  setProofOfBankAccountDetailsDoc,
  setCrsSelfCertificationFormDoc,
  setCompanyConstitutionDoc,
  hydrateDocuments,
  clearDocumentType,
  resetAllDocuments,
} = docBorrowerSlice.actions;

export default docBorrowerSlice.reducer;
